import { useState, useEffect, useRef } from 'react';
import { API } from "../../../../Common/lib/api";
import { HiChevronLeft, HiChevronRight, HiSearch } from "react-icons/hi";

import RequestModal from "../Modals/requestModal";

import {
  AdminUpdateUserAttributes,
  AdminGetUser,
} from "../../../../Common/lib/Admin/AdminAPI";

export default function UserRequestsTable() {
  const [verificationRequests, setVerificationRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const [currentPage, setCurrentPage] = useState(0);
  const [allNextTokens, setAllNextTokens] = useState([null]); // Used to handle pagination. Stores all nextTokens from queries in here and allows you to query between pages. First item should be null.
  const [searchValue, setSearchValue] = useState("");

  const [openRequestModal, setOpenRequestModal] = useState(false);

  const searchBarInput = useRef(); // Used to retrieve value from searchbar input box

  useEffect(() => {
    queryRequests();
  }, [currentPage, searchValue]);

  /**
   * Handles querying user verification requests. 
   */
  async function queryRequests() {
    try {
      const params = {
        category: "VerificationRequest",
        limit: 8,
        offset: currentPage * 8
      };

      if (searchValue.length > 0) {
        params.userInfo__icontains = searchValue;
      }

      const verificationsQuery = await API.logs.list(params);
      const items = verificationsQuery.data.listLogs.items || [];
      const total = verificationsQuery.data.listLogs.total || 0;

      setVerificationRequests(items);
      setTotalCount(total);

      // Track if there are more pages based on total count
      const hasMorePages = (currentPage + 1) * 8 < total;
      if (hasMorePages && allNextTokens[currentPage + 1] === undefined) {
        setAllNextTokens([...allNextTokens, currentPage + 1]);
      }
    } catch (e) {
      console.error("Error fetching verification requests:", e);
      setVerificationRequests([]);
      setTotalCount(0);
    }
  }

  /**
   * Handles approving a user verification request. Sets contributor_access to true, 
   * then deletes the user request. Having contributor_access will allow the user to submit datasets.
   * @param {Object} requestInfo User verification request object. Contains user ID, Date, and info that the user provided.
   */
  async function approveUser(requestInfo) {
    try {
      // Ensure we have the user identifier
      const userId = requestInfo.userID || requestInfo.id || requestInfo.sub;
      if (!userId) {
        console.error('No user identifier found on request:', requestInfo);
        return;
      }

      // Check existing contributor flag before attempting update
      let shouldSet = true;
      try {
        const userRes = await AdminGetUser(userId);
        const attrs = userRes?.UserAttributes || userRes?.Attributes || [];
        const contribAttr = attrs.find((a) => a && (a.Name === 'contributor_access' || a.Name === 'custom:contributor_access'));
        const contribVal = contribAttr?.Value ?? userRes?.contributor_access ?? userRes?.contributorAccess;
        if (contribVal === true || contribVal === 'true' || contribVal === '1' || contribVal === 1) {
          shouldSet = false;
        }
      } catch (e) {
        // If fetching user fails, proceed to set contributor access
        shouldSet = true;
      }

      if (shouldSet) {
        await AdminUpdateUserAttributes(userId, { contributor_access: true });
      }

      // Attempt to delete the verification request on the backend if we have an id
      try {
        if (requestInfo.id) {
          await API.logs.delete(requestInfo.id);
        }
      } catch (e) {
        console.error('Failed to delete verification request on backend:', e);
      }

      // Remove the processed request from the UI
      setVerificationRequests((prev) => prev.filter((r) => {
        const key = r.userID || r.id || r.Date;
        const requestKey = userId || requestInfo.id || requestInfo.Date;
        return key !== requestKey;
      }));
      setTotalCount((c) => Math.max(0, c - 1));
      setOpenRequestModal(false);
    } catch (e) {
      console.error("Error approving user:", e);
    }
  }

  /**
   * Handles declining a user verification request.
   * @param {Object} requestInfo User verification request object. Contains user ID, Date, and info that the user provided.
   */
  async function declineUser(requestInfo) {
    try {
      const userId = requestInfo.userID || requestInfo.id || requestInfo.sub;
      // Attempt to delete the verification request on the backend if we have an id
      try {
        if (requestInfo.id) {
          await API.logs.delete(requestInfo.id);
        }
      } catch (e) {
        console.error('Failed to delete verification request on backend:', e);
      }

      setVerificationRequests((prev) => prev.filter((r) => {
        const key = r.userID || r.id || r.Date;
        const requestKey = userId || requestInfo.id || requestInfo.Date;
        return key !== requestKey;
      }));
      setTotalCount((c) => Math.max(0, c - 1));
      setOpenRequestModal(false);
    } catch (e) {
      console.error("Error declining user:", e);
    }
  }

  /**
   * Handles switching to next page of user requests.
   */
  function nextPage() {
    // Check if there are more pages based on total count
    if ((currentPage + 1) * 8 < totalCount) {
      setCurrentPage(currentPage + 1);
    }
  }

  /**
   * Handles switching to previous page of user requests.
   */
  function previousPage() {
    if (currentPage !== 0) {
      setCurrentPage(currentPage - 1);
    }
  }

  /**
   * Handles setting state variables that will query new user requests with the search value.
   */
  function handleSearch() {
    setSearchValue(searchBarInput.current.value);
    setCurrentPage(0);
    setAllNextTokens([null]);
  }

  return (
    <>
      <div className="flex justify-between">
        <div className="relative flex flex-1 items-center">
          <input
            type="search"
            placeholder="Search Users"
            ref={searchBarInput}
            className="w-full pl-10 pr-3 py-2 outline-none focus:ring-1 focus:ring-inset focus:ring-blue-500 focus:border-blue-500 shadow-sm rounded border border-gray-300"
            onKeyDown={(e) => e.key === 'Enter' ? handleSearch() : null}
          />
          <div className="absolute m-3.5">
            <HiSearch className="w-5 h-5 text-gray-400"/>
          </div>
        </div>
        <button
          className="text-white inline-flex items-center justify-center ml-4 px-8 py-2 border text-sm
                     leading-6 font-medium rounded-md bg-blue-700 hover:bg-blue-500 focus:outline-none 
                     focus:border-blue-700 focus:shadow-outline-blue active:bg-blue-700"
          onClick={() => handleSearch()}
        >
          Search
        </button>
      </div>

      <div className="grid w-full mt-4 grid-cols-1 gap-4 sm:grid-cols-2 ">
        {verificationRequests.length !== 0 ? (
          verificationRequests.map((request) => (
            <div
              key={request.userID || request.id || request.Date}
              className="relative flex items-center space-x-3 rounded-lg bg-gray-100 px-6 py-5 shadow-sm hover:border-gray-400 cursor-pointer"
              onClick={() => { setSelectedRequest(request); setOpenRequestModal(true)}}
            >
              <div className="min-w-0 flex-1">
                <div className="focus:outline-none">
                  <span className="absolute inset-0" aria-hidden="true" />
                  <p onClick={() => { setSelectedRequest(request); setOpenRequestModal(true)}} className="cursor-pointer text-base font-medium text-gray-900">{JSON.parse(request.userInfo).userInfo.name}</p>
                  <p onClick={() => { setSelectedRequest(request); setOpenRequestModal(true) }} className="cursor-pointer truncate text-sm text-gray-500">{JSON.parse(request.userInfo).userInfo.email}</p>
                </div>

                <div className="mt-4 flex">
                  <button
                    onClick={(e) => { e.stopPropagation(); approveUser(request); }}
                    type="button"
                    className="z-10 rounded-md w-full py-1 text-sm font-medium shadow-sm text-gray-700 bg-gray-300 hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Accept
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); declineUser(request); }}
                    type="button"
                    className="z-10 ml-2 rounded-md w-full py-1 text-sm font-medium shadow-sm text-gray-700 bg-gray-300 hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Decline
                  </button>
                </div>
              </div>
            </div>
          ))) : (
          <p className="text-center justify-center col-span-2 mt-4 font-medium text-gray-600">No requests found.</p>
        )}
      </div>

      <RequestModal openRequestModal={openRequestModal} setOpenRequestModal={setOpenRequestModal} requestInfo={selectedRequest} approveUser={approveUser} declineUser={declineUser} />

      {/* Pagination ---------------------------------------------------------------*/}
      {verificationRequests.length !== 0 ? (
        <div className="mt-3 flex gap-2 w-full justify-center items-center">
          <button onClick={() => previousPage()} disabled={currentPage === 0}>
            <HiChevronLeft className="h-6 w-6 text-gray-600 hover:bg-gray-300 rounded" />
          </button>
          <p className="text-gray-700 font-medium">Page {currentPage + 1}</p>
          <button onClick={() => nextPage()} disabled={(currentPage + 1) * 8 >= totalCount}>
            <HiChevronRight className="h-6 w-6 text-gray-600 hover:bg-gray-300 rounded" />
          </button>
        </div>
      ) : (
        <p></p>
      )}
    </>
  );
}